import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { tap, catchError, throwError } from "rxjs";

import { environment } from "../../environments/environment";
import { AuthUserResponse } from "../models/auth-user.model";
import { UserRegisterModel } from "../models/user-register.model";
import { UserLoginRequest } from "../models/user-login.model";
import { WrapperResponse } from "../models/wrapper-response";
import { UserDetailResponse } from "../models/user-detail.model";

@Injectable({ providedIn: "root" })
export class AuthService {
  private static readonly STORAGE_KEYS = {
    LOGGED_IN: "isLoggedIn",
    USER_INFO: "userInfo",
  } as const;

  private static readonly USER_INFO_TTL_MS = 24 * 60 * 60 * 1000; // 24h

  private http = inject(HttpClient);
  private router = inject(Router);
  private apiUrl = `${environment.apiUrl}/users`;

  isLoggedIn = signal<boolean>(false);
  userDetail = signal<UserDetailResponse | null>(null);
  isLoadingUserInfo = signal<boolean>(false);

  initializeAuth() {
    const storedLogged = localStorage.getItem(
      AuthService.STORAGE_KEYS.LOGGED_IN,
    );
    const storedUser = localStorage.getItem(AuthService.STORAGE_KEYS.USER_INFO);

    if (storedLogged !== "true" || !storedUser) {
      return;
    }

    try {
      const parsed = JSON.parse(storedUser);
      const user: UserDetailResponse = parsed.user ?? parsed;
      const expiresAt = parsed.expiresAt
        ? new Date(parsed.expiresAt).getTime()
        : null;

      if (expiresAt && expiresAt < Date.now()) {
        this.cleanUpState();
        return;
      }

      this.isLoggedIn.set(true);
      this.userDetail.set(user);
    } catch {
      this.cleanUpState();
    }
  }

  cleanUpState() {
    this.isLoggedIn.set(false);
    this.userDetail.set(null);
    localStorage.removeItem(AuthService.STORAGE_KEYS.LOGGED_IN);
    localStorage.removeItem(AuthService.STORAGE_KEYS.USER_INFO);
    this.router.navigateByUrl("/");
  }

  setLoginStatus(value: boolean) {
    this.isLoggedIn.set(value);

    if (value) {
      localStorage.setItem(AuthService.STORAGE_KEYS.LOGGED_IN, "true");
    } else {
      localStorage.removeItem(AuthService.STORAGE_KEYS.LOGGED_IN);
      localStorage.removeItem(AuthService.STORAGE_KEYS.USER_INFO);
      this.userDetail.set(null);
    }
  }

  loginAsync(payload: UserLoginRequest) {
    return this.http
      .post<WrapperResponse<AuthUserResponse>>(`${this.apiUrl}/login`, payload)
      .pipe(
        tap((res) => {
          // Mark logged in on successful status, not on presence of data
          if (res.status === 200) {
            this.isLoggedIn.set(true);
            localStorage.setItem(AuthService.STORAGE_KEYS.LOGGED_IN, "true");
          }
        }),
        catchError((err) => {
          this.setLoginStatus(false);
          return throwError(() => err);
        }),
      );
  }

  getUserInfo() {
    this.isLoadingUserInfo.set(true);
    return this.http
      .get<WrapperResponse<UserDetailResponse>>(`${this.apiUrl}/getinfo`)
      .pipe(
        tap((res) => {
          // Decline login if user is ADMIN
          if (res.data.roleName === "ADMIN") {
            this.cleanUpState();
            throw new Error("Akses ditolak untuk pengguna ADMIN");
          }
          this.setUserInfo(res.data);
          this.isLoadingUserInfo.set(false);
        }),
        catchError((err) => {
          this.isLoadingUserInfo.set(false);
          return throwError(() => err);
        }),
      );
  }

  setUserInfo(user: UserDetailResponse) {
    const wrapped = {
      user: user,
      expiresAt: new Date(
        Date.now() + AuthService.USER_INFO_TTL_MS,
      ).toISOString(),
    };

    localStorage.setItem(
      AuthService.STORAGE_KEYS.USER_INFO,
      JSON.stringify(wrapped),
    );
    this.userDetail.set(user);
  }

  forgotPassword(payload: string) {
    return this.http
      .post<WrapperResponse<string>>(`${this.apiUrl}/forgot-password`, {
        mailAddress: payload,
      })
      .pipe(
        tap((res) => {
          if (res.status !== 200) {
            throw new Error(
              res.message || "Gagal mengirim email reset password",
            );
          }
        }),
      );
  }

  changePassword(payload: any) {
    return this.http.put<WrapperResponse<string>>(
      `${this.apiUrl}/change-password`,
      payload,
    );
  }

  activateAccount(token: string) {
    return this.http.post<WrapperResponse<string>>(
      `${this.apiUrl}/activate-account`,
      JSON.stringify(token),
      { headers: { "Content-Type": "application/json" } },
    );
  }

  registerAsync(payload: UserRegisterModel) {
    return this.http.post(`${this.apiUrl}/register`, payload);
  }

  logout$() {
    return this.http
      .post<WrapperResponse<string>>(`${this.apiUrl}/logout`, {})
      .pipe(tap(() => this.cleanUpState()));
  }
}
